import * as React from 'react';
import { useState, useCallback } from 'react';
import { Link } from '@inertiajs/react';
import { Pack, PackItem } from '../../types/landing';
import { getCSRFToken } from '../../utils/csrf';
import { dispatchCartCount } from '../../utils/cartBadge';
import { route } from '../../utils/route';
import { gtagAsync } from '../../utils/tracking';
import { PackChoiceModal } from '../packs/PackChoiceModal';

interface PackCardProps {
  pack: Pack;
  detailUrl?: string;
}

export function PackCard({ pack, detailUrl }: PackCardProps): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const hasChoices = pack.has_choice_groups && pack.choice_groups?.length > 0;

  const formatPrice = (price: number): string => {
    return price.toLocaleString('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const addItemsToCart = useCallback(async (items: { id: number; format_id: number; quantity: number }[]) => {
    setLoading(true);
    try {
      const res = await fetch(route('api.cart.add_item'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items }),
      });
      const data = res.ok ? await res.json() : {};
      setAdded(true);

      // Save pack tracking to localStorage so PacksInCart can display it
      try {
        const stored = localStorage.getItem('cart_packs');
        const tracking = stored ? JSON.parse(stored) : {};
        tracking[pack.id] = {
          addedAt: new Date().toISOString(),
          title: pack.title,
          itemKeys: items.map(i => `${i.id}:${i.format_id}`),
        };
        localStorage.setItem('cart_packs', JSON.stringify(tracking));
      } catch {}

      gtagAsync('event', 'add_pack', {
        pack_id: pack.id,
        pack_name: pack.title,
        pack_value: pack.calculated_price,
        items_count: items.length,
        currency: 'ARS',
      });

      if (data.summary?.count != null) {
        dispatchCartCount(data.summary.count);
      }

      setTimeout(() => setAdded(false), 2000);
    } catch (e) {
      console.error('Error adding pack to cart:', e);
    } finally {
      setLoading(false);
    }
  }, [pack]);

  const handleAddPack = useCallback(async () => {
    if (hasChoices) {
      setShowChoiceModal(true);
      return;
    }
    await addItemsToCart(
      pack.items.map(item => ({
        id: item.product_id,
        format_id: item.product_format_id,
        quantity: item.quantity,
      })),
    );
  }, [hasChoices, pack.items, addItemsToCart]);

  const handleAddWithSelections = useCallback(async (selectedItems: PackItem[]) => {
    const allItems = [
      ...pack.items.map(item => ({
        id: item.product_id,
        format_id: item.product_format_id,
        quantity: item.quantity,
      })),
      ...selectedItems.map(item => ({
        id: item.product_id,
        format_id: item.product_format_id,
        quantity: item.quantity,
      })),
    ];
    setShowChoiceModal(false);
    await addItemsToCart(allItems);
  }, [pack.items, addItemsToCart]);

  const packUrl = detailUrl ?? route('packs.show', { id: pack.slug || pack.id });

  return (
    <div className="pack-card">
      <style>{`
        .pack-card {
          flex: 0 0 280px;
          scroll-snap-align: start;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .pack-card-image-container {
          position: relative;
          width: 100%;
          aspect-ratio: 16/10;
          background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c8 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pack-card-image-link {
          display: block;
          width: 100%;
          height: 100%;
          text-decoration: none;
        }

        .pack-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .pack-card-placeholder {
          font-size: 48px;
          color: #5cb85c;
        }

        .pack-card-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #5cb85c;
          color: #fff;
          font-size: 11px;
          font-weight: 600;
          padding: 4px 8px;
          border-radius: 4px;
        }

        .pack-card-content {
          padding: 16px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .pack-card-title {
          font-size: 16px;
          font-weight: 700;
          color: #333;
          margin-bottom: 6px;
          line-height: 1.3;
        }

        .pack-card-description {
          font-size: 13px;
          color: #666;
          line-height: 1.4;
          margin-bottom: 12px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .pack-card-items-count {
          font-size: 12px;
          color: #999;
          margin-bottom: 12px;
        }

        .pack-card-price {
          font-size: 22px;
          font-weight: 700;
          color: #5cb85c;
          margin-bottom: 12px;
        }

        .pack-card-actions {
          display: flex;
          gap: 8px;
          margin-top: auto;
        }

        .pack-card-details-btn {
          flex: 1;
          padding: 10px;
          font-size: 13px;
          font-weight: 600;
          color: #5cb85c;
          background: #fff;
          border: 2px solid #5cb85c;
          border-radius: 6px;
          cursor: pointer;
          text-decoration: none;
          text-align: center;
          transition: all 0.2s;
        }

        .pack-card-details-btn:hover {
          background: #f0fff0;
        }

        .pack-card-add-btn {
          flex: 1;
          padding: 10px;
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          background: #5cb85c;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .pack-card-add-btn:hover:not(:disabled) {
          background: #4cae4c;
        }

        .pack-card-add-btn:disabled {
          background: #aaa;
          cursor: not-allowed;
        }

        .pack-card-add-btn.added {
          background: #28a745;
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .pack-card {
            flex: 0 0 240px;
          }

          .pack-card-content {
            padding: 12px;
          }

          .pack-card-title {
            font-size: 15px;
          }

          .pack-card-description {
            font-size: 12px;
            margin-bottom: 10px;
          }

          .pack-card-price {
            font-size: 20px;
            margin-bottom: 10px;
          }

          .pack-card-actions {
            flex-direction: column;
            gap: 6px;
          }

          .pack-card-details-btn,
          .pack-card-add-btn {
            padding: 8px;
            font-size: 12px;
          }
        }
      `}</style>

      <div className="pack-card-image-container">
        <Link href={packUrl} className="pack-card-image-link">
          {pack.image_url ? (
            <img
              src={pack.thumb_url || pack.image_url}
              alt={pack.title}
              className="pack-card-image"
              loading="lazy"
            />
          ) : (
            <div className="pack-card-placeholder">
              <i className="fa fa-shopping-basket"></i>
            </div>
          )}
        </Link>
        <div className="pack-card-badge">Pack</div>
      </div>

      <div className="pack-card-content">
        <div className="pack-card-title">{pack.title}</div>
        {pack.description && (
          <div className="pack-card-description">{pack.description}</div>
        )}
        <div className="pack-card-items-count">
          <i className="fa fa-cube" style={{ marginRight: 4 }}></i>
          {pack.items_count} productos
        </div>
        <div className="pack-card-price">
          {hasChoices && <span style={{ fontSize: 14, fontWeight: 400, color: '#999', marginRight: 4 }}>Desde</span>}
          {formatPrice(pack.calculated_price)}
        </div>

        <div className="pack-card-actions">
          <Link href={packUrl} className="pack-card-details-btn">
            Ver detalle
          </Link>
          <button
            type="button"
            className={`pack-card-add-btn ${added ? 'added' : ''}`}
            onClick={handleAddPack}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fa fa-spinner fa-spin"></i>
                Agregando...
              </>
            ) : added ? (
              <>
                <i className="fa fa-check"></i>
                Agregado
              </>
            ) : (
              <>
                <i className="fa fa-cart-plus"></i>
                Agregar
              </>
            )}
          </button>
        </div>
      </div>

      {showChoiceModal && hasChoices && (
        <PackChoiceModal
          title={pack.title}
          choiceGroups={pack.choice_groups}
          fixedItemsTotal={pack.items.reduce((sum, i) => sum + i.subtotal, 0)}
          onConfirm={handleAddWithSelections}
          onClose={() => setShowChoiceModal(false)}
          isLoading={loading}
        />
      )}
    </div>
  );
}
